import { useRef, useState } from 'react'
import type { AzioneFascicolo } from '../../types/fascicoli'

interface HorizontalTimelineProps {
  azioni: AzioneFascicolo[]
  searchFilter?: string
  onDotClick?: (azioneId: string) => void
}

export default function HorizontalTimeline({ azioni, searchFilter, onDotClick }: HorizontalTimelineProps) {
  const [hoveredId, setHoveredId] = useState<string | null>(null)
  const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({})
  const outerRef = useRef<HTMLDivElement>(null)

  if (azioni.length === 0) return null

  // Ordina cronologicamente (vecchio a sinistra, recente a destra)
  const sorted = [...azioni].sort((a, b) =>
    new Date(a.data_azione).getTime() - new Date(b.data_azione).getTime()
  )

  const getColor = (stato: string) => {
    switch (stato) {
      case 'completata': return '#22c55e'
      case 'annullata': return '#9ca3af'
      default: return '#3b82f6'
    }
  }

  const isFiltered = (a: AzioneFascicolo) => {
    if (!searchFilter) return false
    const s = searchFilter.toLowerCase()
    return !(
      a.titolo.toLowerCase().includes(s) ||
      (a.descrizione || '').toLowerCase().includes(s)
    )
  }

  const formatDate = (iso: string) => {
    const d = new Date(iso)
    return d.toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: '2-digit' })
  }

  const handleDotEnter = (e: React.MouseEvent, id: string) => {
    setHoveredId(id)
    if (outerRef.current) {
      const dot = e.currentTarget.getBoundingClientRect()
      const outer = outerRef.current.getBoundingClientRect()
      setTooltipStyle({
        left: dot.left + dot.width / 2 - outer.left,
        top: dot.top - outer.top - 8,
        transform: 'translate(-50%, -100%)',
      })
    }
  }

  const hoveredAzione = hoveredId ? sorted.find(a => a.id === hoveredId) : null

  return (
    <div ref={outerRef} className="relative">
      {/* Tooltip — rendered outside overflow-x-auto so it's never clipped */}
      {hoveredAzione && (
        <div
          className="absolute px-3 py-2 rounded-lg shadow-lg text-xs whitespace-nowrap z-30 pointer-events-none"
          style={{
            ...tooltipStyle,
            backgroundColor: 'var(--color-bg-card, white)',
            border: '1px solid var(--color-border, #e5e7eb)',
            color: 'var(--color-text-primary, #111)',
          }}
        >
          <p className="font-semibold">{hoveredAzione.titolo}</p>
          <p style={{ color: 'var(--color-text-tertiary, #9ca3af)' }}>{formatDate(hoveredAzione.data_azione)}</p>
        </div>
      )}

      <div
        className="flex items-center gap-0 overflow-x-auto py-6 px-4"
        style={{ scrollbarWidth: 'thin' }}
      >
        {/* Linea orizzontale */}
        <div
          className="absolute left-4 right-4 h-0.5"
          style={{
            backgroundColor: 'var(--color-border)',
            top: '50%',
            transform: 'translateY(-50%)',
          }}
        />

        {sorted.map((azione) => {
          const filtered = isFiltered(azione)
          const isAuto = azione.origine === 'automatica'

          return (
            <div
              key={azione.id}
              className="relative flex-shrink-0 flex flex-col items-center"
              style={{
                width: Math.max(40, Math.min(80, 600 / sorted.length)),
                opacity: filtered ? 0.25 : 1,
              }}
              onMouseEnter={(e) => handleDotEnter(e, azione.id)}
              onMouseLeave={() => setHoveredId(null)}
              onClick={() => onDotClick?.(azione.id)}
            >
              {/* Pallino/rombo */}
              <div
                className="relative z-10 cursor-pointer transition-transform hover:scale-125"
                style={{
                  width: 14,
                  height: 14,
                  backgroundColor: getColor(azione.stato),
                  borderRadius: isAuto ? 2 : '50%',
                  transform: isAuto ? 'rotate(45deg)' : undefined,
                  border: '2px solid var(--color-bg-main, #111)',
                }}
              />
            </div>
          )
        })}
      </div>
    </div>
  )
}
